import { useQuery } from '@tanstack/react-query';
import { Lightbulb, ClipboardList, DollarSign } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/utils';
import { getPlanContext } from '@/services/ai-context.service';
import { FormattedText } from '@/components/ui/FormattedText';

interface Props {
  planId: number;
  /**
   * This endpoint generates its summary with an LLM call, so it stays off
   * until the user asks the assistant for something.
   */
  enabled?: boolean;
}

export function PlanContextPanel({ planId, enabled = false }: Props) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['ai-context', 'plan', planId],
    queryFn: () => getPlanContext(planId),
    staleTime: 30_000,
    enabled,
  });

  if (!enabled) return null;

  if (isLoading) return <SkeletonLoader />;

  if (isError || !data) {
    return (
      <div className="p-4 text-sm text-red-600">
        Failed to load plan context. Please try again.
      </div>
    );
  }

  const ps         = data.paymentStatus ?? { totalQuoted: 0, totalPaid: 0, balance: 0 };
  const procedures = data.procedures   ?? [];
  const suggestions = data.suggestions ?? [];
  const paymentPercent =
    ps.totalQuoted > 0
      ? Math.min(Math.round((ps.totalPaid / ps.totalQuoted) * 100), 100)
      : 0;

  return (
    <div className="divide-y divide-slate-100">
      {/* AI Summary */}
      <div className="p-4">
        <div className="rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 p-4 text-white">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-indigo-200 mb-2">
            AI Summary
          </h3>
          <FormattedText text={data.aiSummary} className="text-sm leading-relaxed" />
        </div>
      </div>

      {/* Treatment Info */}
      <div className="px-4 py-3">
        <div className="flex items-center gap-1.5 mb-2">
          <ClipboardList className="h-4 w-4 text-violet-500" />
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Treatment Info
          </h4>
        </div>
        <div className="rounded-md border border-slate-100 bg-slate-50 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-800">{data.patientName}</span>
            {data.stageName && (
              <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200">
                {data.stageName}
              </span>
            )}
          </div>
          {procedures.length > 0 && (
            <div>
              <dt className="text-xs text-slate-400 mb-1">Procedures</dt>
              <div className="flex flex-wrap gap-1">
                {procedures.map((proc, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center rounded-md bg-white px-2 py-0.5 text-xs text-slate-700 ring-1 ring-inset ring-slate-200"
                  >
                    {proc}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Payment Progress */}
      <div className="px-4 py-3">
        <div className="flex items-center gap-1.5 mb-2">
          <DollarSign className="h-4 w-4 text-emerald-500" />
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Payment Progress
          </h4>
        </div>
        <div className="space-y-3">
          {/* Progress bar */}
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-slate-500">
                {formatCurrency(ps.totalPaid)} of {formatCurrency(ps.totalQuoted)}
              </span>
              <span className="font-medium text-slate-700">{paymentPercent}%</span>
            </div>
            <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-500',
                  paymentPercent >= 100
                    ? 'bg-emerald-500'
                    : paymentPercent >= 50
                      ? 'bg-blue-500'
                      : 'bg-amber-500',
                )}
                style={{ width: `${paymentPercent}%` }}
              />
            </div>
          </div>

          {/* Outstanding */}
          {ps.balance > 0 && (
            <div className="flex items-center justify-between rounded-md bg-red-50 px-3 py-2 text-sm">
              <span className="text-red-600">Outstanding</span>
              <span className="font-semibold text-red-700">
                {formatCurrency(ps.balance)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div className="px-4 py-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Lightbulb className="h-4 w-4 text-amber-500" />
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Suggestions
            </h4>
          </div>
          <ul className="space-y-2">
            {suggestions.map((suggestion, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <Lightbulb className="h-4 w-4 mt-0.5 shrink-0 text-amber-400" />
                <span>{suggestion}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SkeletonLoader() {
  return (
    <div className="p-4 space-y-4 animate-pulse">
      <div className="rounded-lg bg-gradient-to-br from-indigo-200 to-purple-200 h-28" />
      <div className="rounded-md bg-slate-100 h-24" />
      <div className="space-y-2">
        <div className="h-3 w-32 bg-slate-200 rounded" />
        <div className="h-2.5 w-full bg-slate-200 rounded-full" />
        <div className="h-10 w-full bg-slate-100 rounded-md" />
      </div>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <div className="h-3 w-24 bg-slate-200 rounded" />
          <div className="h-4 w-full bg-slate-100 rounded" />
        </div>
      ))}
    </div>
  );
}
